import React from 'react';
import {DateTime} from 'luxon';

import {faSearch} from '@fortawesome/free-solid-svg-icons';

import {PaginationScroll} from 'Components/Partials';
import {Checkbox, Select} from 'Components/Form';
import {PrimaryButton} from 'Components/Button';
import {handleMessagePreview, isSameChat, isSameChatCheck} from 'Components/Networking/Sub';

import SupportTicketsApi from 'Services/Api/Admin/Networking/SupportTickets';
import ToApi from 'Services/Api/Networking/To';

import {User, Toast, Socket} from 'Services';
import {translation} from 'Services/TranslationHelpers';
import {mergeObjectsIntoPaginationData} from 'Services/BaseHelpers';

export default class SupportTickets extends React.Component {
    /**
     * @method constructor
     */
    constructor() {
        super();
        this.selectedChatRef = React.createRef();
    }

    /**
     * @var state
     */
    state = {
        showOpen: true,
        showClosed: false,
        showUnassigned: true,
        showAssigned: true,
        tickets: null,
        key: false
    }

    /**
     * @method componentDidMount
     */
    componentDidMount = async () => {
        const {selected_chat} = this.props;

        if (selected_chat) {
            this.handleSelectedChat(selected_chat);
        }

        this.subscribeToBroadcasts();
    }

    /**
     * @method componentDidUpdate
     * @param prevProps
     */
    componentDidUpdate(prevProps) {
        const {selected_chat} = this.props;

        if (selected_chat && prevProps.selected_chat !== selected_chat) {
            this.handleSelectedChat(selected_chat);
        }
    }

    /**
     * @method componentWillUnmount
     */
    componentWillUnmount() {
        this.unsubscribeFromBroadcasts();
    }

    /**
     * @method subscribeToBroadcasts
     */
    subscribeToBroadcasts = () => {
        // Listen for new messages.
        Socket.getConnection().private('networking.' + User.id)
            .listen('NetworkingMessagePosted', (e) => {
                this.handleNewChat({
                    ...e.message.message_to,
                    last_message: e.message
                });
            });
    }

    /**
     * @method unsubscribeFromBroadcasts
     */
    unsubscribeFromBroadcasts = () => {
        // Stop listening for new messages.
        Socket.getConnection().leave('networking.' + User.id);
    }

    /**
     * @method handleSelectedChat
     * @param {object} selected_chat
     */
    handleSelectedChat = (selected_chat) => {
        /*
        TODO
        PaginationScroll resets the data when loading the first page therefore we have to wait
        until after that page has loaded to add this chat. Used a hacky setTimeout solution.
         */
        setTimeout(() => {
            this.handleNewChat(selected_chat);
        }, 500);
    }

    /**
     * @method handleNewChat
     * @param {object} chat
     * @return {Promise<void>}
     */
    handleNewChat = (chat) => {
        const {selected_chat} = this.props;
        const {tickets, showOpen, showClosed, showUnassigned, showAssigned} = this.state;

        // If chat matches the filters then add it.
        if (
            ((chat.additional_data.closed === true && showClosed) || (chat.additional_data.closed === false && showOpen)) &&
            ((chat.additional_data.assigned_to === null && showUnassigned) || (chat.additional_data.assigned_to !== null && showAssigned))
        ) {
            // If selected_chat then mark as read.
            if (selected_chat && isSameChat(selected_chat, chat) && chat.last_message) {
                chat.last_message.read_at = true;
            }

            return this.setState({
                tickets: mergeObjectsIntoPaginationData(tickets, [chat], false, isSameChatCheck())
            });
        }
    }

    /**
     * @method scrollToSelectedChat
     */
    scrollToSelectedChat = () => {
        if (this.selectedChatRef.current !== null) {
            this.selectedChatRef.current.scrollIntoView({behavior: 'smooth'});
        }
    }

    /**
     * @method getApiCall
     * @param {boolean} useData
     * @return {object}
     */
    getApiCall = (useData) => {
        const {showOpen, showClosed, showUnassigned, showAssigned} = this.state;

        let filters = {};

        if (showOpen && !showClosed) {
            filters.closed = 'No'
        } else if (!showOpen && showClosed) {
            filters.closed = 'Yes'
        }

        if (showUnassigned && showAssigned) {
            filters.assigned_to_me_or_null = true;
        } else if (!showUnassigned && showAssigned) {
            filters.assigned_to_me = true;
        } else if (showUnassigned && !showAssigned) {
            filters.assigned_to_null = true;
        }

        return (page) => SupportTicketsApi.get(null, {
            page,
            ...filters
        });
    }

    /**
     * @method setSearchSelectedChat
     * @param {string} id
     */
    setSearchSelectedChat = async (id) => {
        const {setSelectedChat} = this.props;

        const response = await ToApi.get(id, {
            message_to_type: 'App\\Models\\SupportTicket'
        });

        setSelectedChat(response.data.data);
    }

    /**
     * @method handleFilterUpdate
     * @param {string} name
     * @param {boolean} value
     */
    handleFilterUpdate = (name, value) => {
        const {key} = this.state;

        this.setState({
            [name]: value,
            key: !key
        });
    }

    /**
     * @method handleUpdate
     * @param {uuid} id
     * @param {object} data
     */
    handleUpdate = async (id, data) => {
        const {key} = this.state;

        const response = await SupportTicketsApi.patch(id, data);

        if (!response.success) {
            Toast.error();

            return;
        }

        // Reload this support-tickets tab.
        this.setState({
            key: !key
        });

        // Deselect the chat so that the system-message detailing this update loads in.
        this.props.setSelectedChat(null);
    }

    /**
     * @method sortTickets
     * @param {object} chat_a
     * @param {object} chat_b
     * @return {integer}
     */
    sortTickets = (chat_a, chat_b) => {
        // If both have no messages then leave as is.
        if (!chat_a.last_message && !chat_b.last_message) {
            return 1;
        }

        // Put chats with no messages on bottom.
        if (!chat_a.last_message) {
            return -1;
        }

        if (!chat_b.last_message) {
            return 1;
        }

        // If both have same message time then leave as is.
        if (chat_a.last_message.created_at.iso_string === chat_b.last_message.created_at.iso_string) {
            return 1;
        }

        // Order by last_message date.
        var x = DateTime.fromISO(chat_a.last_message.created_at.iso_string, {zone: 'UTC'});
        var y = DateTime.fromISO(chat_b.last_message.created_at.iso_string, {zone: 'UTC'});

        return y > x;
    }

    /**
     * @method render
     * @return {*}
     */
    render() {
        const {useGroups} = this.props;

        return (
            <div className="h-full flex flex-col">
                {this.renderFilters()}
                {this.renderSearchBar()}
                {this.renderTickets()}
            </div>
        )
    }

    /**
     * @method renderFilters
     * @return {*}
     */
    renderFilters() {
        const {showOpen, showClosed, showUnassigned, showAssigned} = this.state;

        return (
            <div className="grid grid-cols-2 gap-4 text-center border bg-gray-200 p-4 mb-2">
                <Checkbox
                    containerClassName="justify-end"
                    label="Open"
                    value={showOpen}
                    id="showOpen"
                    onChange={(v) => this.handleFilterUpdate('showOpen', v)}
                />

                <Checkbox
                    containerClassName="justify-end"
                    label="Closed"
                    value={showClosed}
                    id="showClosed"
                    onChange={(v) => this.handleFilterUpdate('showClosed', v)}
                />

                <Checkbox
                    containerClassName="justify-end"
                    label="Unassigned"
                    value={showUnassigned}
                    id="showUnassigned"
                    onChange={(v) => this.handleFilterUpdate('showUnassigned', v)}
                />

                <Checkbox
                    containerClassName="justify-end"
                    label="Mine"
                    value={showAssigned}
                    id="showAssigned"
                    onChange={(v) => this.handleFilterUpdate('showAssigned', v)}
                />
            </div>
        );
    }

    /**
     * @method renderSearchBar
     * @return {*}
     */
    renderSearchBar() {
        const {setSelectedChat} = this.props;

        return (
            <div className="w-full mb-2 p-2 bg-gray-200">
                <Select
                    containerClassName="w-full"
                    onChange={(v) => this.setSearchSelectedChat(v)}
                    searchCallback={(data) => SupportTicketsApi.get(null, data)}
                    searchLabelKey="name"
                    placeholder={translation('networking_frontend', 'search_name')}
                    isAsync
                    icon={faSearch}
                />
            </div>
        );
    }

    /**
     * @method renderTickets
     * @return {*}
     */
    renderTickets() {
        const {showOpen, showClosed, showUnassigned, showAssigned, tickets, key} = this.state;

        if ((!showOpen && !showClosed) || (!showUnassigned && !showAssigned)) {
            return (
                <p className="px-2 text-center font-bold mt-4">
                    Please select from the filters above.
                </p>
            );
        }

        let apiCall = this.getApiCall(false);

        return (
            <PaginationScroll
                scrollDown={true}
                apiCall={apiCall}
                updateData={(data) => this.setState({tickets: data})}
                data={tickets}
                containerClassName="bg-white"
                key={key}
                sameObjectCheck={isSameChatCheck()}
            >
                {tickets && tickets.data.length === 0 &&
                    <div className="p-4 font-bold text-center">
                        No support tickets.
                    </div>
                }

                {tickets && tickets.data
                    .sort(this.sortTickets)
                    .map((chat, i) => (this.renderTicket(chat)))}
            </PaginationScroll>
        );
    }

    /**
     * @method renderTicket
     * @param {object} chat
     * @return {*}
     */
    renderTicket(chat) {
        const {selected_chat, setSelectedChat} = this.props;

        let is_selected_chat = (selected_chat && isSameChat(selected_chat, chat));

        let is_unread = (chat.last_message && chat.last_message.user_from_id !== User.id && chat.last_message.read_at === null);

        return (
            <div
                className={`
                    mt-1 mb-1 first:mt-0 last:mb-0 p-4 border-l-8 
                    relative cursor-pointer overflow-visible
                    flex flex-col xl:flex-row justify-start items-center gap-4
                    ${is_selected_chat ?
                        'bg-blue-500 text-white' :
                        is_unread ? 'bg-green-400' : 'bg-gray-200'}
                    border-red-600
                    hover:bg-blue-500 hover:text-white
                `}
                onClick={() => setSelectedChat(chat)}
                ref={is_selected_chat ? this.selectedChatRef : null}
                key={chat.type + '_' + chat.id}
            >
                <div className="w-full">
                    <p className="font-bold text-center text-black">
                        {chat.name}
                    </p>

                    {chat.subtitle && chat.subtitle !== '' &&
                        <p className="text-center text-black text-sm">
                            {chat.subtitle}
                        </p>
                    }

                    <p className="text-center text-black text-sm">
                        {!chat.additional_data.assigned_to ? 'Unassigned' : 'Assigned To Me'} 

                        {chat.additional_data.closed ? ' - Closed' : ' - Open'}

                        {is_unread &&
                            <>
                                {" - "}
                                <span className="text-red-500 font-bold">
                                    {translation('networking_frontend', 'unread_message')}
                                </span>
                            </>
                        }
                    </p>

                    <div className="flex flex-row justify-center items-center gap-4 mt-2 w-full">
                        <PrimaryButton
                            text={!chat.additional_data.assigned_to ? 'Take' : 'Leave'}
                            onClick={(e) => {
                                e.stopPropagation();

                                this.handleUpdate(chat.id, {
                                    closed: chat.additional_data.closed,
                                    assigned_to: !chat.additional_data.assigned_to ? User.id : null
                                });
                            }}
                        />

                        <PrimaryButton
                            text={chat.additional_data.closed ? 'Open' : 'Close'}
                            onClick={(e) => {
                                e.stopPropagation();

                                this.handleUpdate(chat.id, {
                                    closed: !chat.additional_data.closed,
                                    assigned_to: chat.additional_data.assigned_to
                                });
                            }}
                        />
                    </div>
                </div>
            </div>
        );
    }
}
